import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { getSessionId } from "../lib/session";
import { formatCountdown } from "../lib/useCountdown";

/**
 * HomeGlitchCountdowns — three cryptic countdowns under the homepage CTA
 * row. NO hint at what each event does — they are glitches and revealing
 * them spoils the surprise. The codename, hex tag, and time-to-next-strike
 * is all the user gets. If a window is currently active we just show
 * "// LIVE //" to bait curious viewers into clicking around.
 */
const SLOTS = [
  { code: "Σ-02",  endpoint: "/cursor-royale/status" },  // 02:00 UTC
  { code: "Σ-18",  endpoint: "/blackout/status"      },  // 18:00 UTC
];

export default function HomeGlitchCountdowns() {
  const [states, setStates] = useState({}); // { endpoint: status }
  const [, force] = useState(0);

  const fetchAll = useCallback(() => {
    const sid = getSessionId();
    Promise.all(SLOTS.map((s) =>
      api.get(s.endpoint, { headers: { "X-Session-Id": sid } })
        .then((r) => [s.endpoint, r.data])
        .catch(() => [s.endpoint, null])
    )).then((pairs) => {
      const next = {};
      for (const [k, v] of pairs) if (v) next[k] = v;
      setStates(next);
    });
  }, []);

  useEffect(() => {
    fetchAll();
    const i = setInterval(fetchAll, 60 * 1000); // refresh once a minute
    return () => clearInterval(i);
  }, [fetchAll]);

  // Re-render every second so the countdown ticks without re-polling.
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      data-testid="home-glitch-countdowns"
      className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 font-mono text-[10px] tracking-[0.3em] uppercase text-white/35 select-none"
    >
      {SLOTS.map((slot) => {
        const s = states[slot.endpoint];
        if (!s) return (
          <span key={slot.code} className="opacity-50">// {slot.code} · — //</span>
        );
        const targetIso = s.active ? s.ends_at : s.started_at;
        let ms = targetIso ? new Date(targetIso).getTime() - Date.now() : 0;
        // If the window's already over (in 24h grace) the started_at is in
        // the past — point at tomorrow's window instead so the countdown
        // always counts toward the next strike.
        if (!s.active && ms <= 0) ms += 24 * 60 * 60 * 1000;
        const label = s.active ? "LIVE" : formatCountdown(ms);
        return (
          <span
            key={slot.code}
            data-testid={`glitch-${slot.code}`}
            className={s.active ? "text-white" : "hover:text-white/55 transition-colors"}
            title={s.active ? "// glitch is live somewhere on this site //" : "// stay alert //"}
          >
            // {slot.code} · {label} //
          </span>
        );
      })}
    </div>
  );
}
